import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { Bell, BellOff, Check, CheckCheck, Calendar, Users, Newspaper, MessageSquare, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

const TYPE_META = {
  event_new: { I: Calendar, color: "bg-wine-50 text-wine-700", label: "Événement" },
  event_participation: { I: Calendar, color: "bg-emerald-50 text-emerald-700", label: "Participation" },
  member_new: { I: Users, color: "bg-blue-50 text-blue-700", label: "Nouveau membre" },
  news_new: { I: Newspaper, color: "bg-amber-50 text-amber-700", label: "Actualité" },
  message: { I: MessageSquare, color: "bg-indigo-50 text-indigo-700", label: "Message" },
};

const PREF_LABELS = {
  event_new: "Nouveaux événements",
  event_participation: "Mes participations (confirmations, rappels)",
  member_new: "Nouveaux membres de la communauté",
  news_new: "Nouvelles actualités",
  message: "Messages reçus",
};

export default function Notifications() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);

  const load = () => api.get("/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/notification-prefs").then((r) => setPrefs(r.data)); }, []);

  const markRead = async (n) => { await api.post(`/notifications/${n.id}/mark-read`); load(); };
  const markAll = async () => { await api.post("/notifications/mark-all-read"); load(); toast.success("Tout marqué comme lu"); };
  const togglePref = async (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    await api.patch("/notification-prefs", { [k]: next[k] });
  };

  const unread = items.filter((n) => !n.read_at).length;

  return (
    <PortalLayout sidebar="member">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Notifications {unread > 0 && <span className="ml-2 inline-flex items-center rounded-full bg-wine-600 px-2.5 py-1 align-middle text-xs font-bold text-white">{unread}</span>}</h1>
          <p className="mt-1 text-neutral-600">{t("portal_notifications_01")}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={markAll} disabled={!unread} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40" data-testid="notif-mark-all"><CheckCheck className="h-3.5 w-3.5" /> Tout marquer comme lu</button>
          <button onClick={() => setShowPrefs(!showPrefs)} className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 bg-wine-50 px-4 py-2 text-xs font-semibold text-wine-700 hover:bg-wine-100" data-testid="notif-prefs-toggle"><SettingsIcon className="h-3.5 w-3.5" />{t("portal_notifications_02")}</button>
        </div>
      </div>

      {showPrefs && prefs && (
        <div className="mt-5 rounded-2xl border border-wine-100 bg-wine-50/40 p-5" data-testid="notif-prefs-panel">
          <h2 className="text-sm font-bold text-navy">{t("portal_notifications_02")}</h2>
          <p className="mt-1 text-xs text-neutral-600">{t("portal_notifications_03")}</p>
          <div className="mt-3 space-y-2">
            {Object.keys(PREF_LABELS).map((k) => (
              <label key={k} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg bg-white px-4 py-2.5 text-sm">
                <span className="font-medium text-neutral-800">{PREF_LABELS[k]}</span>
                <input type="checkbox" checked={!!prefs[k]} onChange={() => togglePref(k)} className="h-4 w-4 accent-wine-500" data-testid={`pref-${k}`} />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {items.length === 0 && <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/40 px-6 py-12 text-center text-sm text-neutral-500" data-testid="notif-empty"><BellOff className="mx-auto mb-2 h-6 w-6 text-neutral-400" />{t("portal_notifications_04")}</div>}
        {items.map((n) => {
          const M = TYPE_META[n.type] || TYPE_META.event_new;
          const I = M.I;
          return (
            <div key={n.id} className={`flex items-start gap-3 rounded-2xl border p-4 transition-all ${n.read_at ? "border-neutral-100 bg-white" : "border-wine-200 bg-wine-50/40"}`} data-testid={`notif-${n.id}`}>
              <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${M.color}`}><I className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${M.color}`}>{M.label}</span>
                  {!n.read_at && <span className="inline-flex h-2 w-2 rounded-full bg-wine-500" />}
                </div>
                <div className="mt-1 text-sm font-bold text-neutral-900">{n.title}</div>
                {n.body && <div className="text-xs text-neutral-600">{n.body}</div>}
                <div className="mt-1.5 flex items-center gap-3 text-[10px] text-neutral-500">
                  <span>{new Date(n.created_at).toLocaleString("fr")}</span>
                  {n.link && <Link to={n.link} className="font-semibold text-wine-600 hover:underline" data-testid={`notif-link-${n.id}`}>{t("portal_notifications_05")}</Link>}
                </div>
              </div>
              {!n.read_at && <button onClick={() => markRead(n)} className="rounded-full p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-wine-600" title="Marquer comme lu" data-testid={`notif-read-${n.id}`}><Check className="h-4 w-4" /></button>}
            </div>
          );
        })}
      </div>
    </PortalLayout>
  );
}
